import { useEffect, useMemo, useState } from "react";
import { Loader2, Save, RotateCcw, CheckCircle2, AlertTriangle, Link2, Link2Off, Database, Cpu, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { getLeadLayoutFields } from "../config/recordConfigs";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

export default function IntegrationMapping({ lockProvider = null }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [providers, setProviders] = useState([]);
  const [leadFields, setLeadFields] = useState([]);
  const [active, setActive] = useState(null);
  const [edits, setEdits] = useState({}); // { provider: { target: source } }
  const [openGroups, setOpenGroups] = useState({}); // collapsed by default
  const toggleGroup = (g) => setOpenGroups((s) => ({ ...s, [g]: !s[g] }));

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integration-mapping");
      setProviders(data.providers || []);
      // Lead fields (labels/order/groups) come from the Lead record layout so the mapping
      // pages auto-update on any field add/remove/relocate; internal-logic notes come from the API.
      const internalMap = Object.fromEntries((data.lead_fields || []).map((f) => [f.key, f.internal]));
      setLeadFields(getLeadLayoutFields().map((f) => ({ ...f, internal: internalMap[f.key] || null })));
      const e = {};
      (data.providers || []).forEach((p) => { e[p.provider] = { ...(p.mapping || {}) }; });
      setEdits(e);
      setActive((prev) => lockProvider || prev || (data.providers?.[0]?.provider ?? null));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (lockProvider) setActive(lockProvider); }, [lockProvider]);

  const prov = useMemo(() => providers.find((p) => p.provider === active), [providers, active]);
  const mapping = edits[active] || {};
  const incoming = prov?.incoming_fields || [];
  const sampleOf = (field) => incoming.find((f) => f.field === field)?.sample;

  const usedSources = new Set(Object.values(mapping).filter(Boolean));
  const unmappedIncoming = incoming.filter((f) => !usedSources.has(f.field));

  // Per-provider lookup so every row can show BOTH providers' mapping side by side.
  const providerMap = useMemo(
    () => Object.fromEntries(providers.map((p) => [p.provider, p])),
    [providers]
  );
  // The current (edited) source a provider maps to a given Lead field.
  const srcFor = (providerName, target) =>
    providerName === active
      ? (edits[active]?.[target] || "")
      : (providerMap[providerName]?.mapping?.[target] || "");
  const sampleFor = (providerName, source) => {
    if (!source) return null;
    const f = (providerMap[providerName]?.incoming_fields || []).find((x) => x.field === source);
    return f ? f.sample : null;
  };

  const setField = (target, source) =>
    setEdits((p) => ({ ...p, [active]: { ...p[active], [target]: source || null } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/integration-mapping/${active}`, { mapping: mapping });
      toast.success(`${active} mapping saved — applied on future promotions`);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const reset = async () => {
    try {
      await api.post(`/integration-mapping/${active}/reset`);
      toast.success(`${active} mapping reset to defaults`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const grouped = useMemo(() => {
    const g = {};
    leadFields.forEach((f) => { (g[f.group] = g[f.group] || []).push(f); });
    return g;
  }, [leadFields]);

  const CHANNELS = {
    PropertyRadar: { title: "PR Field Mapping", channel: "Off-Market", blurb: "PropertyRadar supplies OFF-MARKET land parcels — owner + parcel intelligence, no list price." },
    RealEstateAPI: { title: "REAPI Field Mapping", channel: "On-Market", blurb: "RealEstateAPI supplies ON-MARKET active MLS land listings — list price, status and days on market." },
  };
  const cfg = lockProvider ? CHANNELS[lockProvider] : null;
  const displayProviders = lockProvider ? providers.filter((p) => p.provider === lockProvider) : providers;
  const single = displayProviders.length === 1;
  const colCls = single ? "md:col-span-4" : "md:col-span-3";

  if (loading) {
    return <div className="p-8 flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading mapping…</div>;
  }

  return (
    <div className="p-6 sm:p-8 space-y-6 max-w-7xl" data-testid="integration-mapping-page">
      <div>
        <span className="block text-xs uppercase tracking-[0.15em] text-primary mb-2">
          System · Data Integrations{cfg ? ` · ${cfg.channel} Deals` : ""}
        </span>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">{cfg ? cfg.title : "Integration Mapping"}</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          {cfg
            ? <>{cfg.blurb} Every field on the <span className="font-medium text-foreground">Inbound Lead</span> record is listed below, grouped exactly as it appears on the Lead page, with an <span className="font-medium text-foreground">Internal Logic</span> note when Arqitek fills or transforms the value itself. Choose the incoming field for each Lead field; saved changes apply the next time a candidate is promoted to a Lead.</>
            : <>Every field on the <span className="font-medium text-foreground">Inbound Lead</span> record is listed below, grouped exactly as it appears on the Lead page. Each row shows what <span className="font-medium text-foreground">both</span> PropertyRadar and RealEstateAPI map to that field, plus an <span className="font-medium text-foreground">Internal Logic</span> note when Arqitek fills or transforms the value itself. Saved changes apply the next time a candidate is promoted to a Lead.</>}
        </p>
      </div>

      {/* Provider tabs — hidden on a channel-locked page */}
      {!lockProvider && (
        <div className="flex gap-2 flex-wrap">
          {providers.map((p) => (
            <button
              key={p.provider}
              onClick={() => setActive(p.provider)}
              data-testid={`provider-tab-${p.provider}`}
              className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${active === p.provider ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/40"}`}
            >
              {p.provider}
              <span className="ml-2 text-xs opacity-70">{p.mapped_count} mapped · {p.unmapped_incoming_count} unused</span>
            </button>
          ))}
        </div>
      )}

      {prov && (
        <>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Database className="h-3.5 w-3.5" />
              {incoming.length > 0
                ? `Incoming fields sampled from the latest ${prov.provider} record`
                : `No ${prov.provider} records yet — run the AI Buy Box to pull sample data`}
              {prov.is_customized && <Badge className="ml-1 bg-amber-50 text-amber-700 border-amber-200">Customized</Badge>}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={reset} data-testid="reset-mapping-btn">
                <RotateCcw className="h-4 w-4 mr-1" /> Reset to defaults
              </Button>
              <Button size="sm" onClick={save} disabled={saving} data-testid="save-mapping-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Save mapping
              </Button>
            </div>
          </div>

          {/* Mapping table by group — collapsible, collapsed by default */}
          {Object.entries(grouped).map(([group, fields]) => {
            const open = !!openGroups[group];
            const gid = group.replace(/\s+/g, "-").toLowerCase();
            return (
            <Card key={group} className="p-0 overflow-hidden">
              <button
                type="button"
                onClick={() => toggleGroup(group)}
                aria-expanded={open}
                data-testid={`map-group-toggle-${gid}`}
                className="w-full flex items-center justify-between gap-2 px-4 py-2.5 bg-muted/40 hover:bg-muted/60 transition-colors text-xs font-semibold uppercase tracking-wide text-muted-foreground"
              >
                <span>{group}<span className="ml-1.5 opacity-60 normal-case">({fields.length} fields)</span></span>
                <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
              {open && (
              <div data-testid={`map-group-body-${gid}`}>
              <div className="hidden md:grid grid-cols-12 items-center gap-3 px-4 py-2 border-b border-border bg-muted/20 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                <div className={single ? "col-span-4" : "col-span-3"} title="The field on your Inbound Lead object">Lead Field</div>
                <div className={single ? "col-span-4" : "col-span-3"} title="This field's value is created or transformed by Arqitek's internal logic (derivations, GIS overrides, computed summaries) — not taken directly from a provider">Internal Logic</div>
                {displayProviders.map((p) => (
                  <div key={p.provider} className={single ? "col-span-4" : "col-span-3"} title={`What ${p.provider} maps to this Lead field`}>
                    {p.provider}
                    {p.provider === active
                      ? <span className="ml-1 text-primary normal-case">· editing</span>
                      : <span className="ml-1 opacity-60 normal-case">· reference</span>}
                  </div>
                ))}
              </div>
              <div className="divide-y divide-border">
                {fields.map((lf) => (
                  <div key={lf.key} className="grid grid-cols-1 md:grid-cols-12 items-start gap-3 px-4 py-3" data-testid={`map-row-${lf.key}`}>
                    <div className={colCls}>
                      <div className="text-sm font-medium">{lf.label}</div>
                      <div className="text-[11px] text-muted-foreground font-mono">{lf.key}{lf.gis ? " · GIS-authoritative" : ""}</div>
                    </div>
                    <div className={colCls}>
                      <div className="md:hidden text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Internal Logic</div>
                      {lf.internal ? (
                        <div className="flex items-start gap-1.5" data-testid={`map-internal-${lf.key}`}>
                          <Badge className="bg-sky-50 text-sky-700 border-sky-200 gap-1 shrink-0"><Cpu className="h-3 w-3" /> Internal</Badge>
                          <span className="text-[11px] text-muted-foreground leading-snug">{lf.internal}</span>
                        </div>
                      ) : (
                        <span className="text-[11px] text-muted-foreground/40" data-testid={`map-internal-none-${lf.key}`}>—</span>
                      )}
                    </div>
                    {displayProviders.map((p) => {
                      const isActive = p.provider === active;
                      const src = srcFor(p.provider, lf.key);
                      const sample = sampleFor(p.provider, src);
                      return (
                        <div key={p.provider} className={colCls}>
                          <div className="md:hidden text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                            {p.provider}{isActive ? " · editing" : " · reference"}
                          </div>
                          {isActive ? (
                            <select
                              value={src}
                              onChange={(e) => setField(lf.key, e.target.value)}
                              data-testid={`map-select-${p.provider}-${lf.key}`}
                              className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                            >
                              <option value="">— not mapped —</option>
                              {(p.incoming_fields || []).map((f) => (
                                <option key={f.field} value={f.field}>{f.field}</option>
                              ))}
                              {src && !(p.incoming_fields || []).some((f) => f.field === src) && <option value={src}>{src}</option>}
                            </select>
                          ) : (
                            <div className="rounded-md border border-dashed border-border/70 bg-muted/20 px-2 py-1.5 text-sm font-mono truncate" data-testid={`map-readonly-${p.provider}-${lf.key}`} title={src || "not mapped"}>
                              {src || <span className="text-muted-foreground italic font-sans">— not mapped —</span>}
                            </div>
                          )}
                          <div className="mt-1.5 flex items-center gap-2">
                            {src
                              ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 gap-1"><CheckCircle2 className="h-3 w-3" /> Mapped</Badge>
                              : <Badge variant="outline" className="text-muted-foreground gap-1"><Link2Off className="h-3 w-3" /> Unmapped</Badge>}
                            {sample != null && sample !== "" && (
                              <span className="text-[11px] text-muted-foreground truncate" title={sample}>{sample}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
              </div>
              )}
            </Card>
            );
          })}

          {/* Unmapped incoming fields */}
          <Card className="p-4" data-testid="unmapped-incoming">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <span className="font-semibold text-sm">Incoming {prov.provider} fields not mapped to any Lead field ({unmappedIncoming.length})</span>
            </div>
            {unmappedIncoming.length === 0 ? (
              <p className="text-xs text-muted-foreground">Every incoming field is aligned to a Lead field.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                {unmappedIncoming.map((f) => (
                  <div key={f.field} className="flex items-center justify-between gap-2 text-xs border-b border-border/50 py-1">
                    <span className="font-mono text-foreground">{f.field}</span>
                    <span className="text-muted-foreground truncate max-w-[45%]" title={f.sample || ""}>{f.sample}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
